import type { FC } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { AppBar, Box, Button, Divider, Hidden, IconButton, Toolbar, Typography, Stack, Avatar } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { Logo } from '../Logo';
import { accountService } from '../../account.service';
import { getInitials } from '../../utils/get-initials';
import { useTheme } from '@mui/styles';

interface ProfileNavbarProps {
    onSidebarMobileOpen?: () => void;
    isReseller: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        homeLink: {
            textDecoration: 'none',
        },
    })
);

export const ProfileNavbar: FC<ProfileNavbarProps> = ({ onSidebarMobileOpen, isReseller }) => {
    const classes = useStyles();
    const navigate = useNavigate();
    const theme = useTheme();
    const account = accountService.getAccount();

    const logout = () => {
        accountService.logout();
        navigate('/');
    };

    return (
        <AppBar
            elevation={0}
            sx={{
                backgroundColor: 'primary',
                color: 'text.secondary',
            }}
        >
            <Toolbar sx={{ minHeight: 64 }}>
                <Hidden lgUp>
                    <IconButton
                        sx={{ color: theme.palette.primary.contrastText }}
                        onClick={onSidebarMobileOpen}
                        size="large"
                    >
                        <MenuIcon fontSize="small" />
                    </IconButton>
                </Hidden>
                <Hidden lgDown>
                    <RouterLink to="/" className={classes.homeLink}>
                        <Stack direction="row" spacing={1} sx={{ textDecoration: 'none' }}>
                            <Logo />
                            <Typography
                                sx={{
                                    caretColor: 'rgba(0,0,0,0)',
                                    color: theme.palette.primary.contrastText,
                                    whiteSpace: 'nowrap',
                                    textDecoration: 'none',
                                    lineHeight: '40px',
                                }}
                                variant="h6"
                            >
                                LSoft Technologies
                            </Typography>
                            <Typography
                                sx={{
                                    color: 'white',
                                    whiteSpace: 'nowrap',
                                    fontWeight: 100,
                                    lineHeight: '40px',
                                }}
                                variant="h6"
                            >
                                |&nbsp; Customer Support
                            </Typography>
                        </Stack>
                    </RouterLink>
                </Hidden>
                <Box sx={{ flexGrow: 1 }} />
                {account && (
                    <Avatar
                        sx={{
                            height: 42,
                            mr: 2,
                            width: 42,
                            color: theme.palette.primary.main,
                            bgcolor: 'white',
                        }}
                    >
                        {getInitials(`${account.name}`)}
                    </Avatar>
                )}
                {account && (
                    <Typography sx={{ color: theme.palette.primary.contrastText, fontWeight: 300 }}>
                        {isReseller ? 'Reseller ID' : 'Client ID'}: {account.id}
                    </Typography>
                )}
                <Divider
                    orientation="vertical"
                    sx={{
                        height: 32,
                        mx: 2,
                        backgroundColor: theme.palette.primary.contrastText,
                    }}
                />
                <Button
                    sx={{
                        color: theme.palette.primary.contrastText,
                        marginLeft: 1,
                    }}
                    startIcon={<PersonIcon />}
                    onClick={logout}
                >
                    Sign Out
                </Button>
            </Toolbar>
        </AppBar>
    );
};
